import { subscribe } from 'valtio';
import { putLayerConfig } from '~/api/layerConfig';
import { createDocumentSyncController, type DocumentSyncController } from './documentSyncController';
import layerConfigStore, { type LayerConfigStore } from './layerConfig';
import { stateToDocument } from './layerConfig.mapping';
import layerStore from './layers';

// ---------------------------------------------------------------------------
// Thin adapter over the generic `createDocumentSyncController` write machine, for the per-user
// layer config. This module is the ONLY sink for the feature's state — there is no localStorage
// cache; the DB document is the single source of truth.
//
// Adds two things the generic doesn't know about: the CG-752 `layerStore.pausePersistence` guard
// (a download form transiently owns the shared `layerStore`; its wipe must never reach the
// document) and a 3-way subscription topology (see `start` below). See `documentSyncController.ts`
// for the shared write-machine design (baseline diff, single-flight, retain-dirty-on-failure).
// ---------------------------------------------------------------------------

/** Trailing debounce window: a burst of toggles coalesces into one PUT. */
const DEBOUNCE_MS = 1000;
/** A long continuous burst still flushes at least this often. */
const MAX_WAIT_MS = 5000;

interface LayerConfigSyncTransport {
  put: typeof putLayerConfig;
}

export class LayerConfigSyncController {
  private readonly sync: DocumentSyncController;

  constructor(
    private config: LayerConfigStore = layerConfigStore,
    private transport: LayerConfigSyncTransport = { put: putLayerConfig },
    debounceMs: number = DEBOUNCE_MS,
    maxWaitMs: number = MAX_WAIT_MS,
  ) {
    this.sync = createDocumentSyncController({
      canPersist: () => this.config.canPersist,
      getState: () => this.config.getState(),
      toDocument: stateToDocument,
      put: (organizationId, document) => this.transport.put(organizationId, document),
      // Watched state (the doc-store's OWN editable sets, NOT the shared `layerStore`):
      // - the two document-backed editable proxies (`visibleLeafIds` / `pinnedLeafIds`) — the
      //   picker is their sole writer, so render-only mutations of the shared `layerStore` sets
      //   (download/export/data-request/headless/side-panels) never schedule a write. Expansion
      //   (`expandedNodeIds`) is deliberately NOT watched: it is device-local localStorage state
      //   and never part of the document;
      // - the `layerConfigStore` root — Library membership mutations (`addToLibrary` /
      //   `removeFromLibrary`) land in `meta`, which is REASSIGNED on every hydrate, so a
      //   subscription on the root (not on `meta` itself) is what survives; removing a group
      //   with no visible/pinned/expanded entries changes only `meta`, so the set subscriptions
      //   alone would miss it. Over-subscription is harmless — the dirty diff decides whether
      //   anything is actually written.
      subscribe: (onChange) => {
        const unsubVisible = subscribe(this.config.visibleLeafIds, onChange, true);
        const unsubPinned = subscribe(this.config.pinnedLeafIds, onChange, true);
        const unsubRoot = subscribe(this.config, onChange, true);
        return () => {
          unsubVisible();
          unsubPinned();
          unsubRoot();
        };
      },
      onHydrate: (fn) => this.config.onHydrate(fn),
      // CG-752: a download form transiently owns the shared `layerStore`; its wipe must never
      // reach the document — the structural diff self-heals once the form restores.
      extraGuard: () => layerStore.pausePersistence,
      debounceMs,
      maxWaitMs,
    });
  }

  /**
   * Delegate to the generic controller: activate the constructor-wired valtio subscriptions and
   * the store's hydrate lifecycle slot. Idempotent — the React hook may remount.
   */
  start() {
    this.sync.start();
  }

  /** Tear down all subscriptions and cancel any pending flush. */
  stop() {
    this.sync.stop();
  }

  /**
   * Whether the in-memory state differs from the last-persisted document. Self-
   * correcting: a toggle-then-untoggle back to the baseline is NOT dirty. Exposed for a
   * future "saving…" affordance. Always false while persistence is not armed
   * (`canPersist` false) or before the first hydrate.
   */
  isDirty(): boolean {
    return this.sync.isDirty();
  }

  /**
   * Arm/extend the trailing debounce. No-op while persistence is not armed (`canPersist` false)
   * or while a download form owns `layerStore` (CG-752 `pausePersistence`) — its transient wipe
   * must never reach the document; the structural diff self-heals once the form restores.
   */
  scheduleFlush() {
    this.sync.scheduleFlush();
  }

  /**
   * The debounced write: serialize current state at fire time (never a snapshot from
   * schedule time), PUT the whole document, and on success move the baseline to what was
   * sent — then re-diff, because edits may have arrived during the flight, and reschedule
   * if so. On failure the baseline (and thus dirty) is retained; the NEXT flush trigger
   * (change or lifecycle) retries — no hot retry loop against a failing server.
   *
   * Only one PUT is ever in flight, so writes reach the server in dispatch order and the
   * server's last-write-wins upsert needs no ordering token.
   */
  async flush(): Promise<void> {
    return this.sync.flush();
  }

  /**
   * Immediate flush for lifecycle boundaries (route change, tab hidden, unload): cancel the
   * pending debounce and write now. Uses the same normal PUT as the debounced path; on a
   * true page teardown the request may be cancelled, so teardown persistence is best-effort.
   */
  flushNow() {
    this.sync.flushNow();
  }
}

const layerConfigSync = new LayerConfigSyncController();

/**
 * Point the layer config at `organizationId` (`undefined` = no active org). Flushes the previous
 * org's pending edits first — the PUT is addressed to the org that was hydrated — then either
 * bootstraps the new org or, with no org, disarms the document store. `bootstrap` disarms
 * persistence synchronously, so no edit can reach the previous org's document after this returns.
 */
export function switchLayerConfigOrganization(
  organizationId: string | undefined,
  sync: Pick<LayerConfigSyncController, 'flushNow'> = layerConfigSync,
  config: Pick<LayerConfigStore, 'bootstrap' | 'deactivate'> = layerConfigStore,
) {
  sync.flushNow();
  if (organizationId) void config.bootstrap(organizationId);
  else config.deactivate();
}

export default layerConfigSync;
